// Screens for the Quiz Bank and the Training Portal. Pure: data in, HTML out.
// Layout from MOCKUP_QUIZ_BANK_APPROVED.html and MOCKUP_TRAINING_PORTAL_APPROVED.html:
// identity header, tabs, back link, one card with label and value rows.
// Training Portal buttons live in portal-screens.ts. The Quiz Bank is still read only.

import { STATIONS, stationsDone, type StationProgress } from "./field-demo";
import { historyHtml } from "./quiz-bank-screens";
import { NODES, type QuizQuestion } from "./training";
import {
  EARNED_BY_LABEL,
  QUESTION_TARGET,
  initials,
  lotoRowState,
  scoreLine,
  siteNameOf,
  usDate,
  type NodeRow,
  type QuizSummary,
  type Trainee,
} from "./training-view";

export function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}


export function ident(badge: string, title: string, sub: string): string {
  return `<div class="ident"><div class="badge">${esc(badge)}</div><div class="ident-text"><h1>${esc(title)}</h1><div class="sub">${esc(sub)}</div></div></div>`;
}

function row(label: string, value: string, end = ""): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e">${end}</div></div>`;
}

function validText(days: number | null): string {
  return days === null ? "does not expire" : `valid ${days.toLocaleString("en-US")} days`;
}

const LOCKED = '<span class="lock">Locked</span>';

// ---- Shell ----

/** `back`: the field page Admin ID came from. Cancel goes back to it (NAV_AUDIT.md item 8). */
export function signInScreen(error = "", back = ""): string {
  return `<section class="signin">
    <div class="signin-card">
      <div class="brand">SafeTag <span>· Admin</span></div>
      <h1>Sign in</h1>
      <p class="muted">Temporary admin sign-in for the dev build. The tag and PIN sign-in replaces it.</p>
      ${error ? `<div class="note red">${esc(error)}</div>` : ""}
      <form id="signin-form" novalidate>
        <label for="signin-email">Email</label>
        <input id="signin-email" name="email" type="email" autocomplete="username" required>
        <label for="signin-password">Password</label>
        <input id="signin-password" name="password" type="password" autocomplete="current-password" required>
        <button class="prim wide" type="submit">Sign In</button>
      </form>
      ${back.startsWith("/?") ? `<a class="back" href="${esc(back)}" style="margin-top:12px">Cancel</a>` : ""}
    </div>
  </section>`;
}

export function noBadgeScreen(email: string): string {
  return `<section class="signin"><div class="signin-card">
    <h1>No admin badge on this login</h1>
    <p>${esc(email)} signed in, but it carries no admin badge yet.</p>
    <p class="muted">Run <code>safetag_admin_badge.py</code> on the PC, then sign out and back in.</p>
    <button class="prim wide" type="button" data-act="signout">Sign Out</button>
  </div></section>`;
}

/** chip: the site chip (COHESION_WORDS.md Move 2), already built as HTML. Blank when not on a site. */
export function topBar(holderName: string, showHome = false, chip = ""): string {
  const home = showHome ? '<a class="home" href="#/">Home</a>' : "";
  return `<header class="bar"><div class="bar-left">${home}<a class="t" href="#/">SafeTag <span>· Admin</span></a>${chip}</div>
    <div class="bar-right"><span class="who">${esc(holderName)}</span><button class="so" type="button" data-act="signout">SIGN OUT</button></div></header>`;
}

export function loadingScreen(what: string): string {
  return `<div class="loading">Loading ${esc(what)}…</div>`;
}

export function errorScreen(message: string): string {
  return `<div class="note red">${esc(message)}</div>`;
}

// ---- Quiz Bank ----

function quizTabs(summaries: QuizSummary[], activeId: string): string {
  return `<nav class="tabs">${summaries
    .map((s) => `<a href="#/quiz-bank/${s.quiz.quizId}" class="${s.quiz.quizId === activeId ? "on" : ""}">${esc(s.quiz.quizName)}</a>`)
    .join("")}</nav>`;
}

export function quizBankList(summaries: QuizSummary[], activeId: string, flash = ""): string {
  const s = summaries.find((x) => x.quiz.quizId === activeId) ?? summaries[0]!;
  const total = summaries.reduce((n, x) => n + x.questions.length, 0);
  const active = summaries.reduce((n, x) => n + x.activeCount, 0);
  const nodeNum = s.quiz.nodeId.replace("node", "");
  const nodeName = NODES.find((n) => n.nodeId === s.quiz.nodeId)?.nodeName ?? "";
  const countOk = s.activeCount === QUESTION_TARGET;
  const rows = s.questions
    .map(
      (q) => `<a class="q${q.isActive ? "" : " dim"}" href="#/quiz-bank/${s.quiz.quizId}/${esc(q.questionId)}">
        <span class="n">${q.questionNumber}</span>
        <span class="tx">${esc(q.questionText)}</span>
        <span class="sl">Slide ${esc(q.slideRef)}</span>
        <span class="pill ${q.isActive ? "green" : "grey"}">${q.isActive ? "ACTIVE" : "OFF"}</span></a>`,
    )
    .join("");
  return `${ident("QB", "Quiz Bank", `${summaries.length} quizzes · ${total} questions · ${active} active`)}
    ${quizTabs(summaries, s.quiz.quizId)}
    ${flash}
    <div class="box">
      <div class="boxhd"><div class="icon">Q${nodeNum}</div><div class="nm"><b>${esc(s.quiz.quizName)} · Node ${nodeNum}</b><small>${esc(nodeName)} · closes Node ${nodeNum} on a pass</small></div><a class="prim" href="#/quiz-bank/${s.quiz.quizId}/new">Add Question</a></div>
      <div class="rows">
        ${row("Modules", esc(s.modules.join(", ")), LOCKED)}
        ${row("Slide range", `<span class="mono">${s.quiz.slideFrom} to ${s.quiz.slideTo}</span>`, LOCKED)}
        ${row("Pass mark", esc(s.passLine), '<button class="chg" type="button" data-act="pm-open">Change</button>')}
        ${row("Active questions", `<span class="${countOk ? "ok" : "warn-text"}">${s.activeCount} of ${QUESTION_TARGET} target</span>`)}
        ${row("Node valid for", s.validDays === null ? "Does not expire" : `${s.validDays.toLocaleString("en-US")} days`, LOCKED)}
      </div>
    </div>
    <div class="box">
      <div class="qhead"><span>No.</span><span>Question</span><span class="slh">Slide</span><span>Status</span></div>
      ${rows || '<div class="empty">No questions in this quiz yet.</div>'}
    </div>`;
}

export function quizQuestionDetail(summaries: QuizSummary[], s: QuizSummary, q: QuizQuestion, flash = ""): string {
  const edit = `<a class="chg" href="#/quiz-bank/${s.quiz.quizId}/${esc(q.questionId)}/edit">Change</a>`;
  const opts = (["A", "B", "C", "D"] as const)
    .map((k) => {
      const text = q[`option${k}`];
      if (!text) return "";
      const right = q.correctAnswer === k;
      return `<div class="opt${right ? " right" : ""}"><span class="k">${k}</span><span>${esc(text)}</span></div>`;
    })
    .join("");
  return `<a class="back" href="#/quiz-bank/${s.quiz.quizId}">Quiz Bank</a>
    ${ident("QB", "Quiz Bank", `${esc(s.quiz.quizName)} · question ${q.questionNumber}`)}
    ${quizTabs(summaries, s.quiz.quizId)}
    ${flash}
    <div class="box">
      <div class="boxhd"><div class="icon">${q.questionNumber}</div><div class="nm"><b>Question ${q.questionNumber}</b><small>${esc(s.quiz.quizName)} · ${q.questionType === "TF" ? "true or false" : "multiple choice"} · slide ${esc(q.slideRef)}</small></div><button class="prim" type="button" data-act="qb-switch">${q.isActive ? "Turn Off" : "Turn On"}</button></div>
      <div class="rows">
        ${row("Status", q.isActive ? '<span class="ok">Active</span>' : '<span class="muted">Off</span>')}
        ${row("Question", esc(q.questionText), edit)}
        ${row("Options", `<div class="opts">${opts}</div>`, edit)}
        ${row("Correct answer", `<span class="mono">${esc(q.correctAnswer)}</span>`, edit)}
        ${row("Slide reference", `<span class="mono">${esc(q.slideRef)}</span>`, edit)}
        ${row("Question ID", `<span class="mono">${esc(q.questionId)}</span>`, LOCKED)}
        ${row("Edit history", historyHtml(q))}
      </div>
    </div>`;
}

// ---- Training Portal ----

function dots(t: Trainee): string {
  return `<span class="dots">${t.nodes.map((n) => `<i class="d ${n.state === "done" ? "ok" : n.failed ? "bad" : n.expired ? "exp" : ""}"></i>`).join("")}</span>`;
}

/** The first expired node, for the pill and the note. */
function firstExpired(t: Trainee): NodeRow | undefined {
  return t.nodes.find((n) => n.expired);
}

/** Roster status: a failed quiz waiting for correction, an expired node, QEW, or nodes done. */
function statusPill(t: Trainee): string {
  const exp = firstExpired(t);
  if (t.isQew && !exp) return '<span class="pill green">QEW</span>';
  if (exp && !t.failed) return `<span class="pill amber">RETAKE NODE ${exp.nodeId.replace("node", "")}</span>`;
  if (t.failed) return `<span class="pill red">${esc(t.failed.quizId.replace("quiz", "QUIZ "))} · ${t.failed.scorePct}%</span>`;
  return `<span class="pill amber">${t.doneCount} OF ${NODES.length}</span>`;
}

function sitePicker(sites: { siteNumber: string; name: string; qewCount: number }[], current: string): string {
  return `<label class="site-pick" for="site-select">Site
    <select id="site-select" data-act="site">${sites
      .map((s) => `<option value="${s.siteNumber}"${s.siteNumber === current ? " selected" : ""}>${s.siteNumber} · ${esc(s.name)}</option>`)
      .join("")}</select></label>`;
}

export function portalClass(
  sites: { siteNumber: string; name: string; qewCount: number }[],
  siteNumber: string,
  roster: Trainee[],
  parts: { flash: string; session: string; past: string },
): string {
  const name = siteNameOf(siteNumber);
  const qews = roster.filter((t) => t.isQew).length;
  const inTraining = roster.length - qews;
  const rows = roster
    .map(
      (t) => `<a class="q tq" href="#/training/${siteNumber}/${esc(t.qewId)}">
        <span class="av${t.isQew ? " on" : ""}">${esc(initials(t.name))}</span>
        <span class="tx">${esc(t.name)}<small>${esc(t.qewId)}</small></span>
        ${dots(t)}
        ${statusPill(t)}</a>`,
    )
    .join("");
  return `${ident(siteNumber, `Training · ${name.split(",")[0]!}`, `${name} · ${qews} QEWs · ${inTraining} in training`)}
    ${sitePicker(sites, siteNumber)}
    ${parts.flash}
    <h2 class="sec">Session</h2>
    ${parts.session}
    <div class="sec-row"><h2 class="sec">Trainees</h2><a class="nt-add" href="#/training/${siteNumber}/new">Add Trainee</a></div>
    <div class="box">
      <div class="qhead tq"><span></span><span>Name</span><span class="slh">Nodes</span><span>Status</span></div>
      ${rows || '<div class="empty">No one at this site yet. A trainee shows here once the tag is claimed.</div>'}
    </div>
    ${parts.past}`;
}

/** Node 4 open: the two demo JSPs, each ticked with the JSP that did it. */
function stationsHtml(p: StationProgress): string {
  const list = STATIONS.map((st) => {
    const d = p[st.key];
    return d
      ? `<div class="stn"><span class="ok">✓</span>${esc(st.label)} · ${esc(d.jspNumber)}</div>`
      : `<div class="stn"><span class="todo">○</span>${esc(st.label)}</div>`;
  }).join("");
  return `<div class="meta">${stationsDone(p)} of ${STATIONS.length} demo JSPs</div><div class="stns">${list}</div>`;
}

/**
 * LOTO under the nodes (Stage 4b, MOCKUP_SIGNIN_PASS.html, approved 2026-10-10). The last step before Active.
 * Waiting On Verification links to the same Verify LOTO Date form People & Badges uses, and comes back here.
 */
function lotoRow(t: Trainee): string {
  const l = t.loto;
  const state = lotoRowState(l);
  if (state === "verified") {
    const by = l.verifiedByName ? ` by ${esc(l.verifiedByName)}` : "";
    return `<div class="nd done"><span class="c">✓</span><div class="b"><b>LOTO</b><small>Verified ${usDate(l.verifiedOn!)}${by}</small></div><span class="pill green">DONE</span></div>`;
  }
  if (state === "waiting") {
    return `<div class="nd loto"><span class="c lt">LOTO</span><div class="b"><b>LOTO</b><small>Reported ${usDate(l.reportedOn!)} · Waiting On Verification</small></div><a class="chg" href="#/people/p/${encodeURIComponent(t.qewId)}/loto/training">Verify LOTO Date</a></div>`;
  }
  return `<div class="nd loto"><span class="c lt">LOTO</span><div class="b"><b>LOTO</b><small>Not On File · he reports it on his LOTO card</small></div><span class="pill grey">NONE</span></div>`;
}

function nodeRow(n: NodeRow, t: Trainee): string {
  const num = n.nodeId.replace("node", "");
  const closesOn = n.quizId ? `Quiz ${num}` : "Two demo JSPs";
  const head = `<b>Node ${num} · ${esc(n.nodeName)}</b><small>${esc(n.modules.join(", "))} · ${closesOn} · ${validText(n.validDays)}</small>`;
  const base = `#/training/${t.siteNumber}/${esc(t.qewId)}`;
  if (n.state === "done" && n.record) {
    const r = n.record;
    const score = scoreLine(r, n.attempt);
    // Node 4 closed on the demo JSPs shows their numbers (RECORDS_WORDS.md rev 2).
    const how = r.earnedBy === "fieldDemo" && r.evidence ? r.evidence : EARNED_BY_LABEL[r.earnedBy];
    const detail = `<div class="meta muted">${esc(how)}${score ? ` · ${esc(score)}` : ""}${r.sessionId ? ` · ${esc(r.sessionId)}` : ""}${r.closedBy ? ` · by ${esc(r.closedBy)}` : ""}</div>`;
    // A carried-over Node 4 shows its demonstration evidence from the records copy (RECORDS_WORDS.md rev 2).
    const demo = r.nodeId === "node4" && r.demonstration && r.earnedBy !== "fieldDemo" && r.evidence ? `<div class="meta muted">${esc(r.evidence)}</div>` : "";
    return `<div class="nd done"><span class="c">✓</span><div class="b">${head}<div class="meta">Completed ${usDate(r.completedOn)} · ${r.expiresOn ? `expires ${usDate(r.expiresOn)}` : "does not expire"}</div>${detail}${demo}<a class="lnk" href="${base}/date/${esc(r.recordId)}">Edit Completion Date</a></div><span class="pill green">DONE</span></div>`;
  }
  const was = n.expired
    ? `<div class="meta amber">Expired ${usDate(n.expired.expiresOn ?? "")} · completed ${usDate(n.expired.completedOn)}</div><a class="lnk" href="${base}/date/${esc(n.expired.recordId)}">Edit Completion Date</a>`
    : "";
  if (n.state === "open") {
    const f = n.failed;
    const meta = f
      ? `<div class="meta red">Attempt ${f.attemptNumber} · ${f.scorePct}% (${f.questionsCorrect} of ${f.questionsTotal}) · failed · correct it in class</div>`
      : n.quizId
        ? `<div class="meta">Open. A Quiz ${num} pass closes it.</div>`
        : stationsHtml(t.stations);
    return `<div class="nd${f ? " fail" : n.expired ? " exp" : ""}"><span class="c">${num}</span><div class="b">${head}${was}${meta}</div><a class="chg" href="${base}/close/${n.nodeId}">Close by Hand</a></div>`;
  }
  return `<div class="nd lock"><span class="c">${num}</span><div class="b">${head}${was}<div class="meta">Opens after Node ${Number(num) - 1}</div></div><span class="pill grey">LOCKED</span></div>`;
}

export function portalTrainee(t: Trainee, flash = ""): string {
  const name = siteNameOf(t.siteNumber);
  const anyRecord = t.nodes.find((n) => n.record)?.record;
  const exp = firstExpired(t);
  const count = t.isQew && exp ? `${t.doneCount} of ${NODES.length} current` : `${t.doneCount} of ${NODES.length} nodes`;
  const expNum = exp?.nodeId.replace("node", "") ?? "";
  const retake = exp ? (exp.quizId ? `Retake Quiz ${expNum} in class, or Close by Hand.` : "Redo the two demo JSPs, or Close by Hand.") : "";
  const note = !t.isQew
    ? exp
      ? `<div class="note">Node ${expNum} expired ${usDate(exp.expired!.expiresOn ?? "")}. ${retake}</div>`
      : ""
    : exp
      ? `<div class="note">Training complete. Node ${expNum} expired ${usDate(exp.expired!.expiresOn ?? "")}. ${retake} Active status stays.</div>`
      : '<div class="note green">All four nodes done. Training complete.</div>';
  return `<a class="back" href="#/training/${t.siteNumber}">Training</a>
    ${ident(initials(t.name), t.name, `${t.qewId} · ${name} · ${count}`)}
    ${flash}
    ${note}
    <div class="box"><div class="track">${t.nodes.map((n) => nodeRow(n, t)).join("")}${lotoRow(t)}</div></div>
    ${
      t.allCarriedOver && anyRecord
        ? `<h2 class="sec">Admin detail</h2><div class="box"><div class="rows">
          ${row("Earned by", "Carried over")}
          ${row("Old session", `<span class="mono">${esc(anyRecord.sessionId)}</span>`)}
          ${row("Evidence", esc(anyRecord.evidence))}
        </div></div>`
        : ""
    }`;
}

